import { PutCommand, GetCommand, UpdateCommand, QueryCommand, DeleteCommand, ScanCommand } from '@aws-sdk/lib-dynamodb';
import { awsConfig, createAuthedDocumentClient } from '../config/aws';
import { AWSUserProfile as UserProfile } from '../contexts/AWSAuthContext';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

// DynamoDB utility functions for GlowLens
// All functions require an authenticated CognitoUserSession

/**
 * Create or update user profile
 */
export const putUserProfile = async (session: CognitoUserSession, profile: UserProfile): Promise<void> => {
  try {
    const client = createAuthedDocumentClient(session);
    await client.send(new PutCommand({
      TableName: awsConfig.dynamodb.usersTable,
      Item: profile,
    }));
  } catch (error) {
    console.error('Error putting user profile:', error);
    throw error;
  }
};

/**
 * Get user profile by UID
 */
export const getUserProfile = async (session: CognitoUserSession, uid: string): Promise<UserProfile | null> => {
  try {
    const client = createAuthedDocumentClient(session);
    const result = await client.send(new GetCommand({
      TableName: awsConfig.dynamodb.usersTable,
      Key: { uid },
    }));
    
    return (result.Item as UserProfile) || null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

/**
 * Update user profile attributes
 */
export const updateUserProfile = async (session: CognitoUserSession, uid: string, updates: Partial<UserProfile>): Promise<void> => {
  try {
    const client = createAuthedDocumentClient(session);
    // Build update expression
    const updateExpressions: string[] = [];
    const expressionAttributeNames: Record<string, string> = {};
    const expressionAttributeValues: Record<string, any> = {};
    
    Object.entries(updates).forEach(([key, value], index) => {
      const attrName = `#attr${index}`;
      const attrValue = `:val${index}`;
      updateExpressions.push(`${attrName} = ${attrValue}`);
      expressionAttributeNames[attrName] = key;
      expressionAttributeValues[attrValue] = value;
    });
    
    await client.send(new UpdateCommand({
      TableName: awsConfig.dynamodb.usersTable,
      Key: { uid },
      UpdateExpression: `SET ${updateExpressions.join(', ')}`,
      ExpressionAttributeNames: expressionAttributeNames,
      ExpressionAttributeValues: expressionAttributeValues,
    }));
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Delete user profile
 */
export const deleteUserProfile = async (session: CognitoUserSession, uid: string): Promise<void> => {
  try {
    const client = createAuthedDocumentClient(session);
    await client.send(new DeleteCommand({
      TableName: awsConfig.dynamodb.usersTable,
      Key: { uid },
    }));
  } catch (error) {
    console.error('Error deleting user profile:', error);
    throw error;
  }
};

/**
 * Save scan result
 */
export const putScanResult = async (session: CognitoUserSession, scanData: any): Promise<void> => {
  try {
    const client = createAuthedDocumentClient(session);
    await client.send(new PutCommand({
      TableName: awsConfig.dynamodb.scansTable,
      Item: scanData,
    }));
  } catch (error) {
    console.error('Error saving scan result:', error);
    throw error;
  }
};

/**
 * Get user's scans
 */
export const getUserScans = async (session: CognitoUserSession, uid: string, limit: number = 20): Promise<any[]> => {
  try {
    const client = createAuthedDocumentClient(session);
    const result = await client.send(new QueryCommand({
      TableName: awsConfig.dynamodb.scansTable,
      KeyConditionExpression: 'uid = :uid',
      ExpressionAttributeValues: {
        ':uid': uid,
      },
      ScanIndexForward: false, // Sort descending (newest first)
      Limit: limit,
    }));
    
    return result.Items || [];
  } catch (error) {
    console.error('Error getting user scans:', error);
    return [];
  }
};

/**
 * Save lifestyle data
 */
export const putLifestyleData = async (session: CognitoUserSession, lifestyleData: any): Promise<void> => {
  try {
    const client = createAuthedDocumentClient(session);
    await client.send(new PutCommand({
      TableName: awsConfig.dynamodb.lifestyleTable,
      Item: lifestyleData,
    }));
  } catch (error) {
    console.error('Error saving lifestyle data:', error);
    throw error;
  }
};

/**
 * Get user's lifestyle data
 */
export const getUserLifestyleData = async (session: CognitoUserSession, uid: string, limit: number = 30): Promise<any[]> => {
  try {
    const client = createAuthedDocumentClient(session);
    const result = await client.send(new QueryCommand({
      TableName: awsConfig.dynamodb.lifestyleTable,
      KeyConditionExpression: 'uid = :uid',
      ExpressionAttributeValues: {
        ':uid': uid,
      },
      ScanIndexForward: false,
      Limit: limit,
    }));
    
    return result.Items || [];
  } catch (error) {
    console.error('Error getting lifestyle data:', error);
    return [];
  }
};

/**
 * Save referral source
 */
export const putReferral = async (session: CognitoUserSession, referralData: any): Promise<void> => {
  try {
    const client = createAuthedDocumentClient(session);
    await client.send(new PutCommand({
      TableName: awsConfig.dynamodb.referralsTable,
      Item: {
        ...referralData,
        referralId: `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
      },
    }));
  } catch (error) {
    console.error('Error saving referral:', error);
    throw error;
  }
};

/**
 * Find user profile by email (scan-based lookup for identity linking)
 */
export const getUserProfileByEmail = async (session: CognitoUserSession, email: string): Promise<UserProfile | null> => {
  try {
    const client = createAuthedDocumentClient(session);
    const result = await client.send(new ScanCommand({
      TableName: awsConfig.dynamodb.usersTable,
      FilterExpression: 'email = :email',
      ExpressionAttributeValues: {
        ':email': email,
      },
    }));

    return (result.Items?.[0] as UserProfile) || null;
  } catch (error) {
    console.error('Error scanning for user profile by email:', error);
    return null;
  }
};

/**
 * Migrate user data (scans, lifestyle) from one uid to another
 */
export const migrateUserData = async (session: CognitoUserSession, oldUid: string, newUid: string): Promise<void> => {
  // Migrate scans
  const scans = await getUserScans(session, oldUid, 1000);
  for (const scan of scans) {
    await putScanResult(session, { ...scan, uid: newUid });
  }

  // Migrate lifestyle data
  const lifestyle = await getUserLifestyleData(session, oldUid, 1000);
  for (const entry of lifestyle) {
    await putLifestyleData(session, { ...entry, uid: newUid });
  }
};

